import React, { useState, useEffect } from 'react';
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Box,
  Snackbar,
  Alert,
  Collapse,
  Tooltip,
  Switch,
  IconButton,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { NumericFormat } from 'react-number-format';
import { useFormState } from '../hooks/useAppData';
import PaymentModal from '../common/PaymentModal';
import TermsDefinitions from '../common/TermsDefinitions';
import { CELL_HOVER_SPOTLIGHT, BLUE_TOOLTIP_HINT_PROPS, BLUE_TOOLTIP_PROPS } from '../common/tableStyles';
import { INPUT_DEFAULTS } from '../common/inputDefaults';
import apiService from '../../services/apiService';

const ConversionsTab = ({
  userId,
  userInfo,
  inputInfo,
  conversions,
  parts,
  retireYearData,
  distributionSchedule,
  loading,
  error,
  onUpdateInputs,
  onRunCalculations,
  onRefetchUser,
  onViewConversion,
  onViewRecommended,
  recommendedConversion,
  openAssumptionsDialog,
  setOpenAssumptionsDialog,
  onNavigateToTab,
  onToggleTaxSource,
  onToggleUseIRSBrackets
}) => {
  // UI state for conversions tab
  const [inputDialogOpen, setInputDialogOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [ageChangeDialogOpen, setAgeChangeDialogOpen] = useState(false);
  const [taxSourceConfirmOpen, setTaxSourceConfirmOpen] = useState(false);
  const [showSpreadExplain, setShowSpreadExplain] = useState(false);
  const [showTaxDragExplain, setShowTaxDragExplain] = useState(false);
  const [showMedicareExplain, setShowMedicareExplain] = useState(false);
  const [showHiddenContribExplain, setShowHiddenContribExplain] = useState(false);
  const [showTdExplain, setShowTdExplain] = useState(false);
  const [showTcExplain, setShowTcExplain] = useState(false);
  const [localTaxSource, setLocalTaxSource] = useState(inputInfo?.tax_payment_source || 'O');
  const pendingTaxSource = localTaxSource === 'I' ? 'O' : 'I';
  // 2026-04-18: SS torpedo bracket-sizing toggle (replaces SS torpedo banner)
  const [useIRSBracketsConfirmOpen, setUseIRSBracketsConfirmOpen] = useState(false);
  const [localUseIRSBrackets, setLocalUseIRSBrackets] = useState(inputInfo?.use_irs_brackets || 'Y');
  useEffect(() => { setLocalUseIRSBrackets(inputInfo?.use_irs_brackets || 'Y'); }, [inputInfo?.use_irs_brackets]);
  const pendingUseIRSBrackets = localUseIRSBrackets === 'Y' ? 'N' : 'Y';
  const [rmdJustSelected, setRmdJustSelected] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [showIncomeProjections, setShowIncomeProjections] = useState(false);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  useEffect(() => { setLocalTaxSource(inputInfo?.tax_payment_source || 'O'); }, [inputInfo?.tax_payment_source]);
  const [calcCount, setCalcCount] = useState(0);
  const [hoveredConvGroupNum, setHoveredConvGroupNum] = useState(null);

  // Sums for PRE-CONVERSION DISTRIBUTIONS table columns 3-8
  const preConvTotalSS = retireYearData.reduce((sum, row) => sum + (row.ss_benefit || 0), 0);
  const preConvTotalTrad = retireYearData.reduce((sum, row) => sum + (row.trad_dist_opt || 0), 0);
  const preConvTotalRoth = retireYearData.reduce((sum, row) => sum + (row.roth_dist_opt || 0), 0);
  const preConvTotalOther = retireYearData.reduce((sum, row) => sum + ((row.atcf_opt || 0) - (row.trad_dist_opt || 0) - (row.roth_dist_opt || 0) - (row.ss_benefit || 0) + (row.fed_tax_opt || 0)), 0);
  const preConvTotalFedTax = retireYearData.reduce((sum, row) => sum + (row.fed_tax_opt || 0), 0);
  const preConvTotalATCF = retireYearData.reduce((sum, row) => sum + (row.atcf_opt || 0), 0);

  // Calculate user's age as of Dec 31 of current year
  const userCurrentAge = userInfo?.birth_date
    ? new Date().getFullYear() - new Date(userInfo.birth_date).getFullYear()
    : 0;
  const userBirthYear = userInfo?.birth_date ? new Date(userInfo.birth_date).getFullYear() : 0;
  const rmdAge = userBirthYear >= 1960 ? 75 : 73;
  const minDistAge = Math.max(60, userCurrentAge + 1);
  const maxDistAge = Math.max(rmdAge, userCurrentAge + 1);
  const minSSAge = 62;
  const maxSSAge = Math.max(70, userCurrentAge + 1);

  // Form state for input dialog
  const {
    formData: inputForm,
    updateField: updateInputField,
    setFormData: setInputFormData
  } = useFormState(INPUT_DEFAULTS);

  // 2026-09-13: Spouse SS inputs show only for MFJ — separate filers report their own SS,
  // and the engine ignores the stored value for every other status. Must sit after
  // useFormState: reading inputForm above it is a temporal-dead-zone ReferenceError.
  const isMFJ = inputForm.distribution_status === 'M';
  // 2026-09-13: Yellow summary panel reflects the SAVED run, so it reads inputInfo rather
  // than the form. Only shown when a spouse benefit actually exists, so the panel is
  // unchanged for everyone else.
  const showSpouseSS = inputInfo?.distribution_status === 'M' && (inputInfo?.soc_sec_benefit_spouse || 0) > 0;

  // Update input form when data loads
  useEffect(() => {
    if (userInfo) {
      setInputFormData({
        soc_sec_benefit: inputInfo?.soc_sec_benefit?.toString() || INPUT_DEFAULTS.soc_sec_benefit,
        soc_sec_benefit_spouse: inputInfo?.soc_sec_benefit_spouse?.toString() || INPUT_DEFAULTS.soc_sec_benefit_spouse,   // 2026-09-13
        salary: inputInfo?.salary?.toString() || INPUT_DEFAULTS.salary,
        cont_return_assum: inputInfo?.cont_return_assum?.toString() || INPUT_DEFAULTS.cont_return_assum,
        dist_return_assum: inputInfo?.dist_return_assum?.toString() || INPUT_DEFAULTS.dist_return_assum,
        inflation_assum: inputInfo?.inflation_assum?.toString() || INPUT_DEFAULTS.inflation_assum,
        soc_sec_grw_assum: inputInfo?.soc_sec_grw_assum?.toString() || INPUT_DEFAULTS.soc_sec_grw_assum,
        retire_tax_hl: inputInfo?.retire_tax_hl || INPUT_DEFAULTS.retire_tax_hl,
        contribution_status: inputInfo?.contribution_status || INPUT_DEFAULTS.contribution_status,
        distribution_status: inputInfo?.distribution_status || userInfo?.marital_status || INPUT_DEFAULTS.distribution_status,
        life_years: inputInfo?.life_years?.toString() || INPUT_DEFAULTS.life_years,
        trad_savings: userInfo.trad_savings?.toString() || INPUT_DEFAULTS.trad_savings,
        roth_savings: userInfo.roth_savings?.toString() || INPUT_DEFAULTS.roth_savings,
        dist_start_age: (() => {
          const stored = parseInt(inputInfo?.dist_start_age) || parseInt(INPUT_DEFAULTS.dist_start_age);
          if (stored < minDistAge || stored > maxDistAge) return minDistAge.toString();
          return stored.toString();
        })(),
        soc_sec_start_age: (() => {
          const stored = parseInt(inputInfo?.soc_sec_start_age) || parseInt(INPUT_DEFAULTS.soc_sec_start_age);
          if (stored < minSSAge || stored > maxSSAge) return minSSAge.toString();
          return stored.toString();
        })(),
        soc_sec_start_age_spouse: (() => {   // 2026-09-13: same clamp as the primary
          const stored = parseInt(inputInfo?.soc_sec_start_age_spouse) || parseInt(INPUT_DEFAULTS.soc_sec_start_age_spouse);
          if (stored < minSSAge || stored > maxSSAge) return minSSAge.toString();
          return stored.toString();
        })(),
        taxable_inc_when_convert: inputInfo?.taxable_inc_when_convert?.toString() || INPUT_DEFAULTS.taxable_inc_when_convert,
        taxable_inc_when_distribute: inputInfo?.taxable_inc_when_distribute?.toString() || INPUT_DEFAULTS.taxable_inc_when_distribute,
        distribution_method: inputInfo?.distribution_method || INPUT_DEFAULTS.distribution_method,
        beneficiary_taxable_income: inputInfo?.beneficiary_taxable_income?.toString() || INPUT_DEFAULTS.beneficiary_taxable_income,
        beneficiary_filing_status: inputInfo?.beneficiary_filing_status || INPUT_DEFAULTS.beneficiary_filing_status,   // 2026-10-27
        medicare_coverage: inputInfo?.medicare_coverage || INPUT_DEFAULTS.medicare_coverage,
        tax_payment_source: inputInfo?.tax_payment_source || INPUT_DEFAULTS.tax_payment_source,
        tax_drag_assum: inputInfo?.tax_drag_assum?.toString() || INPUT_DEFAULTS.tax_drag_assum,
        dist_reinvest_pct: inputInfo?.dist_reinvest_pct != null ? inputInfo.dist_reinvest_pct.toString() : ((inputInfo?.distribution_method || INPUT_DEFAULTS.distribution_method) === 'R' ? '0.75' : '0.2'),
      });
    }
  }, [inputInfo, userInfo, setInputFormData, minDistAge, minSSAge, maxDistAge, maxSSAge, inputDialogOpen]);

  // Auto-open input dialog if no input data exists
  useEffect(() => {
    if (error && error.includes('No inputs found')) {
      setInputDialogOpen(true);
    }
  }, [error]);

  // Open input dialog when triggered from RECOMMENDATION tab
  useEffect(() => {
    if (openAssumptionsDialog) {
      setInputDialogOpen(true);
      setOpenAssumptionsDialog(false);
    }
  }, [openAssumptionsDialog, setOpenAssumptionsDialog]);

  // Handle input form changes
  const handleInputFormChange = (e) => {
    const { name, value } = e.target;
    updateInputField(name, value);
    // Clear error when user starts typing
    if (errorMessage) {
      setErrorMessage(null);
    }
  };

  // Handle success messages
  const handleSuccess = (message) => {
    setSuccessMessage(message);
  };


  // Handle input submission
  const handleInputSubmit = () => {
    // Clear previous error
    setErrorMessage(null);

    // Validate required fields
    if (inputForm.trad_savings === '' || parseFloat(inputForm.trad_savings) <= 0) {
      setErrorMessage('Traditional Savings must be greater than 0');
      return;
    }
    if (inputForm.roth_savings === '') return;
    if (!inputForm.soc_sec_benefit && inputForm.soc_sec_benefit !== 0) {
      setErrorMessage('Soc Sec Benefit is required');
      return;
    }
    if (!inputForm.dist_start_age || parseFloat(inputForm.dist_start_age) <= 0) {
      setErrorMessage('Distribution Start Age is required');
      return;
    }
    if (!inputForm.soc_sec_start_age || parseFloat(inputForm.soc_sec_start_age) <= 0) {
      setErrorMessage('Social Security Start Age is required');
      return;
    }
    // 2026-09-13: The engine zeroes a spouse benefit when there is no spouse birth date —
    // there is no age to ramp it on. Block here rather than let the user enter a number
    // that quietly does nothing. Spouse benefit itself stays optional (blank = none).
    if (isMFJ && parseFloat(inputForm.soc_sec_benefit_spouse) > 0 && !userInfo?.birth_date_spouse) {
      setErrorMessage('Spouse birth date is required to use a spouse Social Security benefit — add it in your profile.');
      return;
    }
    if (isMFJ && parseFloat(inputForm.soc_sec_benefit_spouse) > 0 &&
        (!inputForm.soc_sec_start_age_spouse || parseFloat(inputForm.soc_sec_start_age_spouse) <= 0)) {
      setErrorMessage('Spouse Social Security Start Age is required');
      return;
    }
    // 2026-10-27: RMD runs end with a 10-year beneficiary phase taxed at the beneficiary's filing status
    if (inputForm.distribution_method === 'R' && !inputForm.beneficiary_filing_status) {
      setErrorMessage('Beneficiary Filing Status is required for RMD');
      return;
    }

    // Check if RMD is selected but age has been changed from RMD start age
    const rmdStartAge = Math.max(rmdAge, minDistAge);
    if (inputForm.distribution_method === 'R' && parseInt(inputForm.dist_start_age) !== rmdStartAge) {
      setAgeChangeDialogOpen(true);
      return;
    }

    setConfirmDialogOpen(true);
  };

  // Handle confirmed input submission
  const handleConfirmSubmit = async () => {
    setConfirmDialogOpen(false);
    const isFirstRun = !conversions || conversions.length === 0;
    try {
      await onUpdateInputs(inputForm);
      setInputDialogOpen(false);
      setRmdJustSelected(false);
      handleSuccess('Inputs updated successfully');

      // Fetch current subscription status from database before checking
      const statusData = await apiService.getSubscriptionStatus(userId);
      setCalcCount(statusData.calc_count);

      // Check if we should show payment modal BEFORE running calculation
      if (statusData.subscription_status !== 'paid' && statusData.calc_count >= 50) {
        setPaymentModalOpen(true);
        return; // Stop here - don't run calculation until they choose payment option
      }

      // Run calculations
      const result = await onRunCalculations(userId);
      handleSuccess('Calculations completed successfully');

      // Update calc count for next time
      setCalcCount(result.calcCount);

      // Refresh user data
      onRefetchUser();

      // Navigate new users to RECOMMENDATION tab after first calculation
      if (isFirstRun && onNavigateToTab) {
        onNavigateToTab(1);
      }
    } catch (err) {
      // 2026-04-03: Show error to user instead of silent failure
      console.error('Calculation error:', err.message);
      setErrorMessage(err.message || 'Calculation failed. Please try again.');
    }
  };

  // Handle income projections toggle
  const handleToggleIncomeProjections = () => {
    setShowIncomeProjections(!showIncomeProjections);
  };

  // 2026-09-27: tax_drag_cont_pv/fv carry the SRC shelter on EVERY run, including Inside,
  // where it is the counterfactual value of funding from Outside instead. These tables show
  // what was actually earned, so the shelter is gated here — the single place this file
  // reads it. The alpha totals need no gate: the engine already applies one.
  const srcShelterPv = (row) =>
    (inputInfo?.tax_payment_source === 'O' ? (row?.tax_drag_cont_pv || 0) : 0);

  // Helper function to get row style for conversions table
  const getConversionsRowStyle = (conv) => {
    // 2026-01-25: Gray out rows where conv_amt is 0 (bracket consumed by taxable income)
    if (conv.conv_amt === 0) {
      return { backgroundColor: '#e8e8e8', opacity: 0.6 };
    }

    // 2026-09-26: one test for both funding sources — the incremental total alpha. The
    // Inside branch already computed exactly this (the SRC shelter is zero there); the
    // Outside branch used conv_irr vs the full portfolio return, which excluded the shelter
    // from the IRR while still measuring against the undragged rate, so a bracket the
    // shelter carries into profit showed red.
    const correspondingPart = parts.find(part => part.conv_group_num === conv.conv_group_num);
    const partAlpha = correspondingPart ? (correspondingPart.conversion_alpha_pv || 0) : 0;
    const convAlpha = conv.conversion_alpha_pv || 0;

    if (correspondingPart && partAlpha === 0) {
      return { backgroundColor: 'transparent' };
    }
    if (partAlpha < 0 && convAlpha < 0) {
      return { backgroundColor: '#ffcdd2' };
    } else if (partAlpha < 0) {
      return { backgroundColor: '#ffebee' };
    }
    return { backgroundColor: '#f1f8e9' };
  };

  // Helper function to get row style for parts table
  const getPartsRowStyle = (part) => {
    // 2026-01-25: Gray out rows where conv_amt is 0 (bracket consumed by taxable income)
    if (part.conv_amt === 0) {
      return { backgroundColor: '#e8e8e8', opacity: 0.6 };
    }

    // 2026-09-26: same single test as the conversions table above.
    const alpha = part.conversion_alpha_pv || 0;
    if (alpha === 0) {
      return { backgroundColor: 'transparent' };
    }
    return { backgroundColor: alpha > 0 ? '#f1f8e9' : '#ffebee' };
  };

  // Helper function to detect if a group is the partial bracket (first group with conv_amt > 0 when taxable_inc_when_convert > 0)
  const isPartialBracket = (convOrPart, dataArray) => {
    if (!inputInfo?.taxable_inc_when_convert || inputInfo.taxable_inc_when_convert <= 0) {
      return false;
    }
    if (convOrPart.conv_amt <= 0) {
      return false;
    }
    // Find the first group with conv_amt > 0
    const firstPositiveGroup = dataArray.find(item => item.conv_amt > 0);
    return firstPositiveGroup && firstPositiveGroup.conv_group_num === convOrPart.conv_group_num;
  };

  // Handle snackbar close
  const handleSnackbarClose = () => {
    setSuccessMessage(null);
  };

  // Get parts table header summary
  const getPartsAccordionSummary = () => 'Marginal Brackets — Analytical Decomposition';

  // CSV export functionality for both tables in one file
  const exportConversionsToCSV = () => {
    if ((!conversions || conversions.length === 0) && (!parts || parts.length === 0)) {
      alert('No data to export');
      return;
    }

    const afMultiple = distributionSchedule?.annuity_factor_multiple?.toFixed(4) || 'N/A';
    let csvContent = '';

    // Add metadata section at the top
    // Row 1: Traditional Savings, Soc Sec Benefit, Filing Status
    const filingStatus = inputInfo?.distribution_status === 'S' ? 'Single' :
                         inputInfo?.distribution_status === 'M' ? 'MFJ' :
                         inputInfo?.distribution_status === 'MS' ? 'MFS live w/spouse' :
                         inputInfo?.distribution_status === 'MD' ? 'MFS live separate' : 'Head House';
    // 2026-09-13: Raw values, not toLocaleString — thousand separators would break the CSV cell
    csvContent += `Traditional Savings:,${userInfo?.trad_savings || ''},${showSpouseSS ? 'SS Benefits:' : 'Soc Sec Benefit:'},${inputInfo?.soc_sec_benefit || ''}${showSpouseSS ? ` + ${inputInfo.soc_sec_benefit_spouse}` : ''},Filing Status:,${filingStatus}\n`;

    // Row 2: Return, Inflation, Years Life
    const returnPct = inputInfo?.dist_return_assum ? (inputInfo.dist_return_assum * 100).toFixed(1) + '%' : '';
    const inflationPct = inputInfo?.inflation_assum ? (inputInfo.inflation_assum * 100).toFixed(1) + '%' : '';
    csvContent += `Return:,${returnPct},Inflation:,${inflationPct},Years Life:,${inputInfo?.life_years || ''}\n`;

    // Row 3: Annual Distribution, Growth Multiple
    const annualDist = distributionSchedule?.distribution || '';
    csvContent += `Annual Distribution:,${annualDist},Growth Multiple:,${afMultiple},,\n`;

    // Add blank line before tables
    csvContent += '\n';

    // First table - Conversions
    if (conversions && conversions.length > 0) {
      csvContent += 'Conversions\n';
      const conversionsHeaders = [
        'Group', 'Tax Bracket', 'Conversion Amount', 'Conv Tax = Investment',
        `Synthetic Roth Cont =Conv Tax x ${afMultiple}`, `Spread GL =(tD - tC) x Conv-Amt x ${afMultiple}`, 'Medicare IRMAA Savings', 'Total Investment After-Tax Payout',
        'Return Multiple', 'IRR', 'Duration',
        'tC Conv Tax Rate', 'tD Dist Tax Rate'
      ].join(',');
      csvContent += conversionsHeaders + '\n';

      conversions.forEach(conv => {
        const row = [
          conv.conv_group_num,
          `${(conv.tax_rate_bucket * 100).toFixed(1)}%`,
          conv.conv_amt,
          conv.conv_tax,
          Math.round((conv.total_after_tax_dist_chg_amt || 0) - (conv.irmaa_savings || 0) - (conv.tax_drag_rmd_fv || 0)),
          Math.round(conv.spread_fv_tw || 0),
          Math.round(conv.irmaa_savings || 0),
          conv.total_after_tax_dist_chg_amt,
          conv.conv_return_multiple.toFixed(2),
          `${(conv.conv_irr * 100).toFixed(2)}%`,
          conv.conv_duration.toFixed(1),
          `${(conv.conv_tax_rate * 100).toFixed(2)}%`,
          `${(conv.conv_dist_tax_rate_tw * 100).toFixed(2)}%`
        ].join(',');
        csvContent += row + '\n';
      });
    }

    // Add blank line between tables
    csvContent += '\n';

    // Second table - Individual Bracket Conversions
    if (parts && parts.length > 0) {
      csvContent += 'Individual Bracket Conversions - Fill Each Bracket Separately\n';
      const partsHeaders = [
        'Group', 'Tax Bracket', 'Conversion Amount', 'Conv Tax = Investment',
        `Synthetic Roth Cont =Conv Tax x ${afMultiple}`, `spread G/L =(tD - tC) x Conv-Amt x ${afMultiple}`, 'Medicare IRMAA Savings', 'Total Investment After-Tax Payout',
        'Return Multiple', 'IRR', 'Duration',
        'tC Conv Tax Rate', 'tD Dist Tax Rate'
      ].join(',');
      csvContent += partsHeaders + '\n';

      parts.forEach(part => {
        const row = [
          part.conv_group_num,
          `${(part.tax_rate_bucket * 100).toFixed(1)}%`,
          part.conv_amt,
          part.conv_tax,
          Math.round((part.total_after_tax_dist_chg_amt || 0) - (part.irmaa_savings || 0) - (part.tax_drag_rmd_fv || 0)),
          Math.round(part.spread_fv_tw || 0),
          Math.round(part.irmaa_savings || 0),
          part.total_after_tax_dist_chg_amt,
          part.conv_return_multiple.toFixed(2),
          `${(part.conv_irr * 100).toFixed(2)}%`,
          part.conv_duration.toFixed(1),
          `${(part.conv_tax_rate * 100).toFixed(2)}%`,
          `${(part.conv_dist_tax_rate_tw * 100).toFixed(2)}%`
        ].join(',');
        csvContent += row + '\n';
      });
    }

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'conversions.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const exportIncomeProjectionsToCSV = () => {
    if (!retireYearData || retireYearData.length === 0) {
      alert('No data to export');
      return;
    }

    let csvContent = '';

    // Add metadata section at the top
    const filingStatus = inputInfo?.distribution_status === 'S' ? 'Single' :
                         inputInfo?.distribution_status === 'M' ? 'MFJ' :
                         inputInfo?.distribution_status === 'MS' ? 'MFS live w/spouse' :
                         inputInfo?.distribution_status === 'MD' ? 'MFS live separate' : 'Head House';
    // 2026-09-13: Raw values, not toLocaleString — thousand separators would break the CSV cell
    csvContent += `Traditional Savings:,${userInfo?.trad_savings || ''},${showSpouseSS ? 'SS Benefits:' : 'Soc Sec Benefit:'},${inputInfo?.soc_sec_benefit || ''}${showSpouseSS ? ` + ${inputInfo.soc_sec_benefit_spouse}` : ''},Filing Status:,${filingStatus}\n`;

    // Row 2: Return, Inflation, Years Life
    const returnPct = inputInfo?.dist_return_assum ? (inputInfo.dist_return_assum * 100).toFixed(1) + '%' : '';
    const inflationPct = inputInfo?.inflation_assum ? (inputInfo.inflation_assum * 100).toFixed(1) + '%' : '';
    csvContent += `Return:,${returnPct},Inflation:,${inflationPct},Years Life:,${inputInfo?.life_years || ''}\n`;

    // Add blank line before table
    csvContent += '\n';

    // Income Projections table
    csvContent += 'Retirement Income Projections - Pre-Conversion\n';
    const headers = [
      'Year', 'Age', 'Soc Sec', 'Trad Dist', 'Roth Dist', 'Other Inc',
      'Fed Tax', 'ATCF', 'SS % Taxed',
      'Trad Dist Tax Rate', 'MTR Adj'
    ].join(',');
    csvContent += headers + '\n';

    retireYearData.forEach(row => {
      const otherInc = row.atcf_opt - (row.trad_dist_opt || 0) - (row.roth_dist_opt || 0) - (row.ss_benefit || 0) + (row.fed_tax_opt || 0);
      const csvRow = [
        new Date(row.year).getFullYear(),
        row.age,
        row.ss_benefit,
        row.trad_dist_opt,
        row.roth_dist_opt,
        otherInc,
        row.fed_tax_opt,
        row.atcf_opt,
        `${(row.pct_ss_taxed_opt * 100).toFixed(1)}%`,
        `${(row.trad_dist_opt_tax_rate * 100).toFixed(1)}%`,
        `${(row.trad_mtr_adj_opt * 100).toFixed(1)}%`
      ].join(',');
      csvContent += csvRow + '\n';
    });

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'inc-Projections.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  // 2026-04-03: Removed loading spinner that replaced entire component (caused white flash)
  // Old data stays visible until new data arrives. Confirm button shows "Calculating..." via loading state.
  // if (loading) {
  //   return (
  //     <Box style={{ textAlign: 'center', marginTop: '50px' }}>
  //       <CircularProgress />
  //       <Typography>Loading...</Typography>
  //     </Box>
  //   );
  // }

  return (
    <>
      {error && !error.includes('No inputs found') && (
        <Alert severity="error" style={{ marginBottom: '20px' }}>
          {error}
        </Alert>
      )}

      {/* Combined Summary + Buttons box */}
      <Box sx={{ border: '2px solid #1976d2', borderRadius: 1, mt: '-2px', mb: 1 }}>
      {/* Yellow: User Info and Assumptions */}
      <Box sx={{ px: 2, pb: 0.5, pt: 0.25, backgroundColor: '#fffde7', '& .MuiTypography-root': { lineHeight: 1.2 } }}>
        <Box display="flex" gap={4} flexWrap="nowrap" marginBottom={0.5}>
            <Box minWidth="250px">
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 0.25 }}>User Information</Typography>
            {userInfo ? (
              <>
                <Box display="flex" justifyContent="space-between" gap={2}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>Email:</Typography>
                  <Typography variant="body2">{userInfo.email}</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" gap={2}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>Birth Date:</Typography>
                  <Typography variant="body2">{userInfo.birth_date}</Typography>
                </Box>
                {['M', 'MS', 'MD'].includes(userInfo.marital_status) && userInfo.birth_date_spouse && (
                  <Box display="flex" justifyContent="space-between" gap={2}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>Spouse Birth Date:</Typography>
                    <Typography variant="body2">{userInfo.birth_date_spouse}</Typography>
                  </Box>
                )}
                <Box display="flex" justifyContent="space-between" gap={2}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>{showSpouseSS ? 'SS Benefits:' : 'Soc Sec Benefit:'}</Typography>
                  <Typography variant="body2">${inputInfo?.soc_sec_benefit?.toLocaleString()}{showSpouseSS ? ` + $${inputInfo.soc_sec_benefit_spouse.toLocaleString()}` : ''}</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" gap={2}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>Traditional Savings:</Typography>
                  <Typography variant="body2">${userInfo.trad_savings?.toLocaleString()}</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" gap={2}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>Roth Savings:</Typography>
                  <Typography variant="body2">${userInfo.roth_savings?.toLocaleString()}</Typography>
                </Box>
                {inputInfo && (
                  <Box display="flex" justifyContent="space-between" gap={2}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>Filing Status:</Typography>
                    <Typography variant="body2">{inputInfo.distribution_status === 'S' ? 'Single' : inputInfo.distribution_status === 'M' ? 'MFJ' : inputInfo.distribution_status === 'MS' ? 'MFS live w/spouse' : inputInfo.distribution_status === 'MD' ? 'MFS live separate' : 'Head House'}</Typography>
                  </Box>
                )}
              </>
            ) : (
              <Typography>Loading user information...</Typography>
            )}
          </Box>
          <Box minWidth="250px">
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 0.25 }}>Assumptions</Typography>
            {inputInfo ? (
              <>
                <Box display="flex" justifyContent="space-between" gap={2}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>Returns in Retirement:</Typography>
                  <Typography variant="body2">{(inputInfo.dist_return_assum * 100).toFixed(1)}%</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" gap={2}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>Inflation:</Typography>
                  <Typography variant="body2">{(inputInfo.inflation_assum * 100).toFixed(1)}%</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" gap={2}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>Distribution Start Age:</Typography>
                  <Typography variant="body2">{inputInfo.dist_start_age}</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" gap={2}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>{showSpouseSS ? 'Soc Sec Start Ages:' : 'Soc Sec Start Age:'}</Typography>
                  <Typography variant="body2">{inputInfo.soc_sec_start_age}{showSpouseSS ? ` & ${inputInfo.soc_sec_start_age_spouse}` : ''}</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" gap={2}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>Life Years:</Typography>
                  <Typography variant="body2">{inputInfo.life_years}</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" gap={2}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>Tax Payment:</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{inputInfo.tax_payment_source === 'I' ? 'Inside Traditional' : 'Outside (Cash)'}</Typography>
                </Box>
              </>
            ) : (
              <Typography>No inputs set</Typography>
            )}
          </Box>
          <Box minWidth="250px">
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 0.25 }}>Taxable Income - 'Other'</Typography>
            {inputInfo ? (
              <>
                <Box display="flex" justifyContent="space-between" gap={2}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>When Converting:</Typography>
                  <Typography variant="body2">${inputInfo.taxable_inc_when_convert?.toLocaleString() || '0'}</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" gap={2}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>During Retirement:</Typography>
                  <Typography variant="body2">${inputInfo.taxable_inc_when_distribute?.toLocaleString() || '0'}{inputInfo.taxable_inc_when_distribute > 0 && <sup>†</sup>}</Typography>
                </Box>
                {inputInfo.distribution_method === 'R' && (
                  <Box display="flex" justifyContent="space-between" gap={2}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>Beneficiary 10 yr:</Typography>
                    <Typography variant="body2">${inputInfo.beneficiary_taxable_income?.toLocaleString() || '0'}{inputInfo.beneficiary_taxable_income > 0 && <sup>†</sup>}</Typography>
                  </Box>
                )}
              </>
            ) : (
              <Typography>No inputs set</Typography>
            )}
          </Box>
          <Box minWidth="250px" sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 0.25 }}>Distributions - {inputInfo?.distribution_method === 'R' ? 'RMDs Only' : 'Fixed Annuity'}</Typography>
          {distributionSchedule ? (
            <>
              {inputInfo?.distribution_method !== 'R' ? (
                <Box display="flex" justifyContent="space-between" gap={2}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>Annual Distribution:</Typography>
                  <Typography variant="body2">${distributionSchedule.distribution?.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</Typography>
                </Box>
              ) : (
                <Box display="flex" justifyContent="space-between" gap={2}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>Annual Distributions:</Typography>
                  <Typography variant="body2">yr-by-yr below</Typography>
                </Box>
              )}
              {(() => {
                const preConvActive = retireYearData?.some(r => r.pct_ss_taxed_opt > 0 && r.pct_ss_taxed_opt < 0.85);
                const firstTriggering = !preConvActive && conversions
                  ? conversions.find(c => c.conv_group_num > 0 && c.dist_ss_torpedo_active_post)
                  : null;
                const lastBracket = conversions && conversions.length > 0
                  ? conversions[conversions.length - 1]
                  : null;
                if (!preConvActive && !firstTriggering && !lastBracket) return null;
                const valueText = preConvActive
                  ? 'active'
                  : firstTriggering
                    ? `start ${(firstTriggering.tax_rate_bucket * 100).toFixed(0)}% conversion`
                    : `only ${(lastBracket.tax_rate_bucket * 100).toFixed(0)}% conversion`;
                const tooltipBody = preConvActive ? (
                  <Box sx={{ p: 0.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>Distribution-year only.</Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>Your projected pre-conversion distributions trigger Social Security taxation: each $1 of taxable distribution adds $1.50–$1.85 to AGI, lifting your effective distribution-year tax rate to roughly 1.5×–1.85× your bracket rate (e.g., 22.2% on a 12% bracket, 40.7% on a 22% bracket). Conversions today lock in a low MTRC against this elevated future MTRD — exactly the situation where Roth conversions deliver the largest spread arbitrage.</Typography>
                    <Typography variant="caption" sx={{ fontStyle: 'italic' }}>Distinct from the conversion-year SS torpedo (when receiving SS during the conversion year), which raises conversion tax and is shown separately in the conversion table.</Typography>
                  </Box>
                ) : firstTriggering ? (
                  <Box sx={{ p: 0.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>Conversion-driven entry into the SS Tax Zone.</Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>Your pre-conversion baseline has Social Security fully taxed (or untaxed) every year. The {(firstTriggering.tax_rate_bucket * 100).toFixed(0)}% bracket conversion is the smallest one large enough to shrink future Traditional distributions into the partial-SS-taxation zone. Once there, each $1 of further distribution adds $1.50–$1.85 to AGI — so the marginal value of converting additional dollars jumps sharply at this threshold.</Typography>
                    <Typography variant="caption" sx={{ fontStyle: 'italic' }}>Distribution-year only. Distinct from the conversion-year SS torpedo, which raises conversion tax and is shown separately in the conversion table.</Typography>
                  </Box>
                ) : (
                  <Box sx={{ p: 0.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>SS Tax Zone escape concentrated in the final bracket.</Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>Your traditional balance is large enough that no intermediate bracket shrinks future distributions out of the fully-taxed (85%-cap) SS zone. Only the final {(lastBracket.tax_rate_bucket * 100).toFixed(0)}% bracket — which fully drains the traditional account — eliminates the SS torpedo. The entire SS tax-reduction benefit is absorbed in this last bracket conversion.</Typography>
                    <Typography variant="caption" sx={{ fontStyle: 'italic' }}>Distribution-year only. Distinct from the conversion-year SS torpedo, which raises conversion tax and is shown separately in the conversion table.</Typography>
                  </Box>
                );
                return (
                  <Box display="flex" justifyContent="space-between" gap={2} sx={{ mt: 'auto' }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>SS Tax Zone:</Typography>
                    <Tooltip arrow {...BLUE_TOOLTIP_HINT_PROPS} title={tooltipBody}>
                      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, cursor: 'help' }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'success.main' }}>{valueText}</Typography>
                        <InfoIcon sx={{ fontSize: 14, color: 'success.main' }} />
                      </Box>
                    </Tooltip>
                  </Box>
                );
              })()}
            </>
          ) : (
            <Typography>No distribution data</Typography>
          )}
        </Box>
      </Box>
      </Box>

      <Box sx={{ borderTop: '2px solid #1976d2', px: 2, pt: '1px', pb: 0.25, backgroundColor: '#e8e8e8' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setInputDialogOpen(true)}
            disabled={loading}
          >
            Edit Assumptions & Re-Run
          </Button>
          {onViewRecommended && conversions && conversions.length > 0 && (
            recommendedConversion ? (
              <Button
                variant="contained"
                color="success"
                onClick={onViewRecommended}
                disabled={loading}
              >
                Show Recommended Conversion
              </Button>
            ) : (
              <Typography variant="body2" sx={{ fontWeight: 600, color: 'error.main', alignSelf: 'center' }}>
                No profitable conversion at these assumptions
              </Typography>
            )
          )}
        </Box>
        <Box>
          <Button
            variant="contained"
            color="primary"
            onClick={handleToggleIncomeProjections}
            sx={{ fontWeight: 'bold' }}
            endIcon={<ExpandMoreIcon />}
            disabled={loading || retireYearData.length === 0}
          >
            {showIncomeProjections ? 'Hide Future Distributions' : 'Yr-by-Yr Pre-Conversion Distributions'}
          </Button>
        </Box>
      </Box>
      </Box>
      </Box>

      {/* Income Projections Table */}
      <Collapse in={showIncomeProjections} timeout="auto" unmountOnExit>
        <Box style={{ marginTop: '-4px', marginBottom: '20px' }}>
          <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" sx={{ margin: 0 }}>
              Retirement Income Projections  - Pre-Conversion
            </Typography>
            <Button
              variant="text"
              color="primary"
              onClick={exportIncomeProjectionsToCSV}
              size="small"
              disabled={loading || retireYearData.length === 0}
              style={{ textTransform: 'none', fontSize: '0.875rem' }}
            >
              Export CSV
            </Button>
          </Box>
          <TableContainer component={Paper}>
            <Table size="small" sx={{ '& .MuiTableCell-root': { px: 0.5, py: 0.25 }, '& tbody td:hover': CELL_HOVER_SPOTLIGHT }}>
              <TableHead sx={{ '& .MuiTableCell-root': { py: 0, lineHeight: 1.1, borderBottom: '2px solid #000' } }}>
                <TableRow>
                  <TableCell align="center">Year</TableCell>
                  <TableCell align="center">Age</TableCell>
                  <TableCell align="right">Soc Sec<br />Benefit</TableCell>
                  <TableCell align="right" sx={{ backgroundColor: '#e3f2fd' }}>Traditional<br />Distribution</TableCell>
                  <TableCell align="right">Roth<br />Distribution</TableCell>
                  <TableCell align="right">Other<br />Inc</TableCell>
                  <TableCell align="right">Federal<br />Tax</TableCell>
                  <TableCell align="right">After Tax<br />Cash Flow</TableCell>
                  <TableCell align="center">Soc Sec<br />Pct Taxed</TableCell>
                  <TableCell align="center">Trad Dist<br />Tax Rate</TableCell>
                  <TableCell align="center">MTR<br />Adj</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {retireYearData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={12}>No income projection data available</TableCell>
                  </TableRow>
                ) : (
                  retireYearData
                    .map((row, index) => {
                      const benThreshold = inputInfo?.distribution_method === 'R' ? retireYearData.length - 10 : retireYearData.length;
                      const isBeneficiary = index >= benThreshold;
                      return (
                      <TableRow key={`retire-${row.year}-${row.age}`} sx={isBeneficiary ? { backgroundColor: '#f3e5f5' } : {}}>
                        <TableCell align="right">{new Date(row.year).getFullYear()}</TableCell>
                        <TableCell align="right">{row.age}</TableCell>
                        <TableCell align="right">
                          ${row.ss_benefit.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                        </TableCell>
                        <TableCell align="right" sx={{ backgroundColor: '#e3f2fd' }}>
                          ${row.trad_dist_opt.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                        </TableCell>
                        <TableCell align="right">
                          ${row.roth_dist_opt.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                        </TableCell>
                        <TableCell align="right">
                          ${(Math.round(row.atcf_opt - (row.trad_dist_opt || 0) - (row.roth_dist_opt || 0) - (row.ss_benefit || 0) + (row.fed_tax_opt || 0)) || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                        </TableCell>
                        <TableCell align="right">
                          ${row.fed_tax_opt.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                        </TableCell>
                        <TableCell align="right">
                          ${row.atcf_opt.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                        </TableCell>
                        <TableCell align="right" sx={{ backgroundColor: row.pct_ss_taxed_opt > 0 && row.pct_ss_taxed_opt < 0.85 ? '#e8f5e9' : 'inherit' }}>{(row.pct_ss_taxed_opt * 100).toFixed(1)}%</TableCell>
                        <TableCell align="right">{(row.trad_dist_opt_tax_rate * 100).toFixed(1)}%</TableCell>
                        <TableCell align="right" sx={{ backgroundColor: row.pct_ss_taxed_opt > 0 && row.pct_ss_taxed_opt < 0.85 ? '#e8f5e9' : 'inherit' }}>{(row.trad_mtr_adj_opt * 100).toFixed(1)}%</TableCell>
                      </TableRow>
                      );
                    })
                )}
                {retireYearData.length > 0 && (
                  <TableRow sx={{ '& .MuiTableCell-root': { borderTop: '2px solid #000', fontWeight: 'bold' } }}>
                    <TableCell colSpan={2}>Total</TableCell>
                    <TableCell align="right">${preConvTotalSS.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</TableCell>
                    <TableCell align="right" sx={{ backgroundColor: '#e3f2fd' }}>${preConvTotalTrad.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</TableCell>
                    <TableCell align="right">${preConvTotalRoth.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</TableCell>
                    <TableCell align="right">${(Math.round(preConvTotalOther) || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</TableCell>
                    <TableCell align="right">${preConvTotalFedTax.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</TableCell>
                    <TableCell align="right">${preConvTotalATCF.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</TableCell>
                    <TableCell />
                    <TableCell />
                    <TableCell />
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {inputInfo?.distribution_method === 'R' && (
            <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.5 }}>
              Purple rows = beneficiary period.
            </Typography>
          )}
        </Box>
      </Collapse>

      {/* Input Dialog */}
      <Dialog open={inputDialogOpen} onClose={() => {
        setInputDialogOpen(false);
        setErrorMessage(null);
        setRmdJustSelected(false);
      }}>
        <DialogTitle sx={{ textAlign: 'center', backgroundColor: '#e3f2fd', py: 0.5 }}>Edit Retirement Data</DialogTitle>
        <DialogContent>
          {errorMessage && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMessage(null)}>
              {errorMessage}
            </Alert>
          )}
          <Typography variant="subtitle2" sx={{ mt: 0, mb: -0.5, fontWeight: 'bold', textAlign: 'center', backgroundColor: '#e8e8e8', mx: -3, px: 3, py: 0.5 }}>Savings & Income</Typography>
          <NumericFormat
            sx={{ '& .MuiInput-input': { pb: 0 } }}
            label="Traditional Savings"
            name="trad_savings"
            value={inputForm.trad_savings}
            onValueChange={(values) => {
              updateInputField('trad_savings', values.floatValue || '');
            }}
            thousandSeparator=","
            prefix="$"
            customInput={TextField}
            fullWidth
            margin="dense"
            size="small"
            variant="standard"
            required
          />
          <NumericFormat
            sx={{ '& .MuiInput-input': { pb: 0 } }}
            label="Roth Savings"
            name="roth_savings"
            value={inputForm.roth_savings}
            onValueChange={(values) => {
              updateInputField('roth_savings', values.floatValue !== undefined ? values.floatValue : '');
            }}
            thousandSeparator=","
            prefix="$"
            customInput={TextField}
            fullWidth
            margin="none"
            size="small"
            variant="standard"
            required
          />
          {/* 2026-09-13: Spouse SS paired on the same row for MFJ; non-MFJ renders as before */}
          <Box display="flex" gap={1.5}>
            <NumericFormat
              sx={{ mt: 0.5, '& .MuiInput-input': { pb: 0 } }}
              label={isMFJ ? <>Your SS Benefit - <strong>ANNUAL</strong></> : <>Social Security Benefit - <strong>ANNUAL</strong></>}
              name="soc_sec_benefit"
              value={inputForm.soc_sec_benefit}
              onValueChange={(values) => {
                updateInputField('soc_sec_benefit', values.floatValue || '');
                if (errorMessage) {
                  setErrorMessage(null);
                }
              }}
              thousandSeparator=","
              prefix="$"
              customInput={TextField}
              fullWidth
              margin="none"
              size="small"
              variant="standard"
              required
            />
            {isMFJ && (
              <NumericFormat
                sx={{ mt: 0.5, '& .MuiInput-input': { pb: 0 } }}
                label={<>Spouse SS Benefit - <strong>ANNUAL</strong></>}
                name="soc_sec_benefit_spouse"
                value={inputForm.soc_sec_benefit_spouse}
                onValueChange={(values) => updateInputField('soc_sec_benefit_spouse', values.floatValue ?? '')}
                thousandSeparator=","
                prefix="$"
                customInput={TextField}
                fullWidth
                margin="none"
                size="small"
                variant="standard"
              />
            )}
          </Box>
          {/* 2026-10-13: Both bases are today's dollars, so the engine grows either from the run year — but only the estimate carries next January's COLA, and only once SSA syncs dashboards in December. */}
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.25 }}>
            If you're collecting now, enter your current annual benefit. If benefits start later, enter the ssa.gov estimate for your claim age — it's already in today's dollars.
          </Typography>
          {((inputForm.soc_sec_benefit > 0 && inputForm.soc_sec_benefit < 6000) ||
            (isMFJ && inputForm.soc_sec_benefit_spouse > 0 && inputForm.soc_sec_benefit_spouse < 6000)) && (
            <Alert severity="warning" sx={{ mt: 1, mb: 1 }}>
              Value seems low. Did you enter a monthly amount? This field requires the ANNUAL benefit.
            </Alert>
          )}
          <NumericFormat
            sx={{ mt: 0.5, '& .MuiInput-input': { pb: 0 } }}
            label="Taxable Income When Converting"
            name="taxable_inc_when_convert"
            value={inputForm.taxable_inc_when_convert}
            onValueChange={(values) => {
              updateInputField('taxable_inc_when_convert', values.floatValue !== undefined ? values.floatValue : '0');
            }}
            thousandSeparator=","
            prefix="$"
            customInput={TextField}
            fullWidth
            margin="none"
            size="small"
            variant="standard"
          />
          <NumericFormat
            sx={{ mt: 0.5, '& .MuiInput-input': { pb: 0 } }}
            label="Taxable Income During Retirement - Annual"
            name="taxable_inc_when_distribute"
            value={inputForm.taxable_inc_when_distribute}
            onValueChange={(values) => {
              updateInputField('taxable_inc_when_distribute', values.floatValue !== undefined ? values.floatValue : '0');
            }}
            thousandSeparator=","
            prefix="$"
            customInput={TextField}
            fullWidth
            margin="none"
            size="small"
            variant="standard"
          />
          {/* <TextField
            label="Taxable Income When Converting"
            name="salary"
            type="number"
            value={inputForm.salary}
            onChange={handleInputFormChange}
            fullWidth
            margin="normal"
            variant="outlined"
            InputLabelProps={{ shrink: true }}
          /> */}
          {/* <TextField
            label="Contribution Return Assumption"
            name="cont_return_assum"
            type="number"
            step="0.01"
            value={inputForm.cont_return_assum}
            onChange={handleInputFormChange}
            fullWidth
            margin="normal"
          /> */}
          <Typography variant="subtitle2" sx={{ mt: 0.5, mb: -0.5, fontWeight: 'bold', textAlign: 'center', backgroundColor: '#e8e8e8', mx: -3, px: 3, py: 0.5 }}>Growth Assumptions</Typography>
          <NumericFormat
            sx={{ '& .MuiInput-input': { pb: 0 } }}
            label="Annual Return - (0.5% to 20%)"
            name="dist_return_assum"
            value={inputForm.dist_return_assum ? (inputForm.dist_return_assum * 100).toFixed(1) : ''}
            onValueChange={(values) => {
              updateInputField('dist_return_assum', values.floatValue ? (values.floatValue / 100).toString() : '');
            }}
            onBlur={(e) => {
              const percentValue = parseFloat(e.target.value);
              if (e.target.value && (percentValue < 0.5 || percentValue > 20)) {
                updateInputField('dist_return_assum', '0.05');
              }
            }}
            suffix="%"
            customInput={TextField}
            fullWidth
            margin="dense"
            size="small"
            variant="standard"
          />
          <NumericFormat
            sx={{ '& .MuiInput-input': { pb: 0 } }}
            label="Inflation - (0.5% to 8%) - grows tax brackets and Std Deduction"
            name="inflation_assum"
            value={inputForm.inflation_assum ? (inputForm.inflation_assum * 100).toFixed(1) : ''}
            onValueChange={(values) => {
              updateInputField('inflation_assum', values.floatValue ? (values.floatValue / 100).toString() : '');
            }}
            onBlur={(e) => {
              const percentValue = parseFloat(e.target.value);
              if (e.target.value && (percentValue < 0.5 || percentValue > 8)) {
                updateInputField('inflation_assum', '0.015');
              }
            }}
            suffix="%"
            customInput={TextField}
            fullWidth
            margin="none"
            size="small"
            variant="standard"
          />
          <NumericFormat
            sx={{ mt: 0.5, '& .MuiInput-input': { pb: 0 } }}
            label="Soc Sec Growth - (0.5% to 8%)"
            name="soc_sec_grw_assum"
            value={inputForm.soc_sec_grw_assum ? (inputForm.soc_sec_grw_assum * 100).toFixed(1) : ''}
            onValueChange={(values) => {
              updateInputField('soc_sec_grw_assum', values.floatValue ? (values.floatValue / 100).toString() : '');
            }}
            onBlur={(e) => {
              const percentValue = parseFloat(e.target.value);
              if (e.target.value && (percentValue < 0.5 || percentValue > 8)) {
                updateInputField('soc_sec_grw_assum', '0.015');
              }
            }}
            suffix="%"
            customInput={TextField}
            fullWidth
            margin="none"
            size="small"
            variant="standard"
          />
          <NumericFormat
            sx={{ mt: 0.5, '& .MuiInput-input': { pb: 0 } }}
            label="Tax Drag on Taxable Account Earnings - (0% to 15%)"
            name="tax_drag_assum"
            value={inputForm.tax_drag_assum ? (inputForm.tax_drag_assum * 100).toFixed(1) : '0'}
            onValueChange={(values) => {
              updateInputField('tax_drag_assum', values.floatValue ? (values.floatValue / 100).toString() : '0');
            }}
            onBlur={(e) => {
              const percentValue = parseFloat(e.target.value);
              if (e.target.value && (percentValue < 0 || percentValue > 15)) {
                updateInputField('tax_drag_assum', '0');
              }
            }}
            suffix="%"
            customInput={TextField}
            fullWidth
            margin="none"
            size="small"
            variant="standard"
          />
          {/* <FormControl fullWidth margin="normal" variant="outlined">
            <InputLabel>Retirement Tax Level</InputLabel>
            <Select
              name="retire_tax_hl"
              value={inputForm.retire_tax_hl}
              onChange={handleInputFormChange}
              label="Retirement Tax Level"
            >
              <MenuItem value={1}>Low</MenuItem>
              <MenuItem value={2}>High</MenuItem>
            </Select>
          </FormControl> */}
          {/* <FormControl fullWidth margin="normal" variant="outlined">
            <InputLabel>Contribution Status</InputLabel>
            <Select
              name="contribution_status"
              value={inputForm.contribution_status}
              onChange={handleInputFormChange}
              label="Contribution Status"
            >
              <MenuItem value="S">Single</MenuItem>
              <MenuItem value="M">Married Filing Jointly</MenuItem>
              <MenuItem value="H">Head of Household</MenuItem>
              <MenuItem value="MS">Married Filing Separately - Live with Spouse</MenuItem>
              <MenuItem value="MD">Married Filing Separately - Live Apart</MenuItem>
            </Select>
          </FormControl> */}
          <Typography variant="subtitle2" sx={{ mt: 0.5, mb: -0.5, fontWeight: 'bold', textAlign: 'center', backgroundColor: '#e8e8e8', mx: -3, px: 3, py: 0.5 }}>Distribution Settings</Typography>
          <FormControl fullWidth margin="dense" size="small" variant="standard" sx={{ '& .MuiInput-input': { pb: 0 } }}>
            <InputLabel>Distribution Method</InputLabel>
            <Select
              name="distribution_method"
              value={inputForm.distribution_method}
              onChange={(e) => {
                handleInputFormChange(e);
                if (e.target.value === 'R') {
                  // RMD selected: set age to RMD start age (73 if born before 1960, 75 if born 1960+)
                  const rmdStartAge = Math.max(rmdAge, minDistAge);
                  updateInputField('dist_start_age', rmdStartAge.toString());
                  updateInputField('dist_reinvest_pct', '0.75');
                  setRmdJustSelected(true);
                } else {
                  // Fixed Annuity selected: reset age to default (62 or user age + 1)
                  updateInputField('dist_start_age', minDistAge.toString());
                  updateInputField('dist_reinvest_pct', '0.2');
                  setRmdJustSelected(false);
                }
              }}
            >
              <MenuItem value="F">Fixed Annuity</MenuItem>
              <MenuItem value="R">RMD Only</MenuItem>
            </Select>
          </FormControl>
          {rmdJustSelected && (
            <Alert severity="info" sx={{ mt: 1, mb: 1 }}>
              RMDs begin at age {Math.max(rmdAge, minDistAge)}. Distribution Start Age has been set accordingly.
            </Alert>
          )}
          {/* 2026-10-27: Beneficiary filing status paired on the same row as beneficiary income */}
          {inputForm.distribution_method === 'R' && (
            <Box display="flex" gap={1.5}>
              <NumericFormat
                sx={{ '& .MuiInput-input': { pb: 0 } }}
                label="Beneficiary Taxable Income - Annual"
                name="beneficiary_taxable_income"
                value={inputForm.beneficiary_taxable_income}
                onValueChange={(values) => {
                  updateInputField('beneficiary_taxable_income', values.floatValue !== undefined ? values.floatValue : '0');
                }}
                thousandSeparator=","
                prefix="$"
                customInput={TextField}
                fullWidth
                margin="none"
                size="small"
                variant="standard"
                required
              />
              <FormControl fullWidth size="small" variant="standard" required sx={{ '& .MuiInput-input': { pb: 0 } }}>
                <InputLabel>Beneficiary Filing Status</InputLabel>
                <Select
                  name="beneficiary_filing_status"
                  value={inputForm.beneficiary_filing_status}
                  onChange={handleInputFormChange}
                >
                  <MenuItem value="S">Single</MenuItem>
                  <MenuItem value="M">Married Filing Jointly</MenuItem>
                  <MenuItem value="H">Head of Household</MenuItem>
                </Select>
              </FormControl>
            </Box>
          )}
          <NumericFormat
            sx={{ mt: 0.5, '& .MuiInput-input': { pb: 0 } }}
            label="Distribution Reinvestment % (0% to 100%)"
            name="dist_reinvest_pct"
            value={inputForm.dist_reinvest_pct ? (inputForm.dist_reinvest_pct * 100).toFixed(0) : '0'}
            onValueChange={(values) => {
              updateInputField('dist_reinvest_pct', values.floatValue != null ? (values.floatValue / 100).toString() : '0');
            }}
            onBlur={(e) => {
              const percentValue = parseFloat(e.target.value);
              if (e.target.value && (percentValue < 0 || percentValue > 100)) {
                updateInputField('dist_reinvest_pct', inputForm.distribution_method === 'R' ? '0.75' : '0.2');
              }
            }}
            suffix="%"
            customInput={TextField}
            fullWidth
            margin="none"
            size="small"
            variant="standard"
          />
          <FormControl fullWidth margin="none" size="small" variant="standard" sx={{ mt: 0.5, '& .MuiInput-input': { pb: 0 } }}>
            <InputLabel>Filing Status</InputLabel>
            <Select
              name="distribution_status"
              value={inputForm.distribution_status}
              onChange={handleInputFormChange}
            >
              <MenuItem value="S">Single</MenuItem>
              <MenuItem value="M">Married Filing Jointly</MenuItem>
              <MenuItem value="H">Head of Household</MenuItem>
              <MenuItem value="MS">Married Filing Separately - Live with Spouse</MenuItem>
              <MenuItem value="MD">Married Filing Separately - Live Apart</MenuItem>
            </Select>
          </FormControl>
          <TextField
            sx={{ mt: 0.5, '& .MuiInput-input': { pb: 0 } }}
            label="Life Expectancy - Years"
            name="life_years"
            type="number"
            value={inputForm.life_years}
            onChange={handleInputFormChange}
            fullWidth
            margin="none"
            size="small"
            variant="standard"
          />
          <TextField
            sx={{ mt: 0.5, '& .MuiInput-input': { pb: 0 } }}
            label={`Distribution Start Age: ${minDistAge} - ${maxDistAge}`}
            name="dist_start_age"
            type="number"
            value={inputForm.dist_start_age}
            onChange={handleInputFormChange}
            fullWidth
            margin="none"
            size="small"
            variant="standard"
            inputProps={{ min: minDistAge, max: maxDistAge }}
          />
          {/* 2026-09-13: Spouse SS start age paired on the same row for MFJ */}
          <Box display="flex" gap={1.5}>
            <TextField
              sx={{ mt: 0.5, '& .MuiInput-input': { pb: 0 } }}
              label={isMFJ ? `Your SS Start Age: ${minSSAge}-${maxSSAge}` : `Social Security Start Age: ${minSSAge} - ${maxSSAge}`}
              name="soc_sec_start_age"
              type="number"
              value={inputForm.soc_sec_start_age}
              onChange={handleInputFormChange}
              fullWidth
              margin="none"
              size="small"
              variant="standard"
              inputProps={{ min: minSSAge, max: maxSSAge }}
            />
            {isMFJ && (
              <TextField
                sx={{ mt: 0.5, '& .MuiInput-input': { pb: 0 } }}
                label={`Spouse SS Start Age: ${minSSAge}-${maxSSAge}`}
                name="soc_sec_start_age_spouse"
                type="number"
                value={inputForm.soc_sec_start_age_spouse}
                onChange={handleInputFormChange}
                fullWidth
                margin="none"
                size="small"
                variant="standard"
                inputProps={{ min: minSSAge, max: maxSSAge }}
              />
            )}
          </Box>
          <Typography variant="subtitle2" sx={{ mt: 0.5, mb: -0.5, fontWeight: 'bold', textAlign: 'center', backgroundColor: '#e8e8e8', mx: -3, px: 3, py: 0.5 }}>Medicare Coverage</Typography>
          <FormControl fullWidth margin="dense" size="small" variant="standard" sx={{ '& .MuiInput-input': { pb: 0 } }}>
            <InputLabel>IRMAA Surcharges</InputLabel>
            <Select
              name="medicare_coverage"
              value={inputForm.medicare_coverage}
              onChange={handleInputFormChange}
            >
              <MenuItem value="BD">Parts B & D</MenuItem>
              <MenuItem value="B">Part B Only</MenuItem>
              <MenuItem value="D">Part D Only</MenuItem>
              <MenuItem value="A">None (Part A Only)</MenuItem>
            </Select>
          </FormControl>
          <Typography variant="subtitle2" sx={{ mt: 0.5, mb: -0.5, fontWeight: 'bold', textAlign: 'center', backgroundColor: '#e8e8e8', mx: -3, px: 3, py: 0.5 }}>Conversion Tax Payment</Typography>
          <FormControl fullWidth margin="dense" size="small" variant="standard" sx={{ '& .MuiInput-input': { pb: 0 } }}>
            <InputLabel>Pay Tax From</InputLabel>
            <Select
              name="tax_payment_source"
              value={inputForm.tax_payment_source}
              onChange={handleInputFormChange}
            >
              <MenuItem value="O">Outside Account</MenuItem>
              <MenuItem value="I">Inside Account</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ mt: -3 }}>
          <Button onClick={() => setInputDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleInputSubmit} color="primary">
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)}>
        <DialogTitle>Confirm Submission and Calculations</DialogTitle>
        <DialogContent>
          <Typography>
            Proceeding with Submit will overwrite Inputs AND also create new Roth Conversion tables, Proceed?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)} disabled={loading}>Cancel</Button>
          <Button onClick={handleConfirmSubmit} color="primary" disabled={loading}>
            {loading ? 'Calculating...' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Age Change Warning Dialog */}
      <Dialog open={ageChangeDialogOpen} onClose={() => setAgeChangeDialogOpen(false)}>
        <DialogTitle>Distribution Age Changed</DialogTitle>
        <DialogContent>
          <Typography>
            Distribution Start Age differs from RMD start age. Distribution Method will be reset to Fixed Annuity. Continue?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            // Reset age back to RMD start age
            const rmdStartAge = Math.max(rmdAge, minDistAge);
            updateInputField('dist_start_age', rmdStartAge.toString());
            setAgeChangeDialogOpen(false);
          }}>Cancel</Button>
          <Button onClick={() => {
            updateInputField('distribution_method', 'F');
            setAgeChangeDialogOpen(false);
            setConfirmDialogOpen(true);
          }} color="primary">
            Continue
          </Button>
        </DialogActions>
      </Dialog>

      {/* Tax Payment Source Confirm Dialog */}
      <Dialog open={taxSourceConfirmOpen} onClose={() => setTaxSourceConfirmOpen(false)}>
        <DialogTitle>Change Conversion Tax Payment Source?</DialogTitle>
        <DialogContent>
          <Typography>
            Switch to <strong>{pendingTaxSource === 'I' ? 'Inside Traditional Account' : 'Outside (Cash)'}</strong> & re-run calculations?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTaxSourceConfirmOpen(false)}>Cancel</Button>
          <Button onClick={async () => { setLocalTaxSource(pendingTaxSource); setTaxSourceConfirmOpen(false); await onToggleTaxSource(pendingTaxSource); }} color="primary">Confirm</Button>
        </DialogActions>
      </Dialog>

      {/* 2026-04-18: SS Torpedo Bracket Sizing Confirm Dialog */}
      <Dialog open={useIRSBracketsConfirmOpen} onClose={() => setUseIRSBracketsConfirmOpen(false)}>
        <DialogTitle>Change Conversion Bracket Sizing?</DialogTitle>
        <DialogContent>
          <Typography>
            Switch to <strong>{pendingUseIRSBrackets === 'Y' ? 'Full IRS Brackets (SS spills to next bracket)' : 'SS-Adjusted (Conversion + Taxable SS fit IRS Bracket)'}</strong> & re-run calculations?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUseIRSBracketsConfirmOpen(false)}>Cancel</Button>
          <Button onClick={async () => { setLocalUseIRSBrackets(pendingUseIRSBrackets); setUseIRSBracketsConfirmOpen(false); await onToggleUseIRSBrackets(pendingUseIRSBrackets); }} color="primary">Confirm</Button>
        </DialogActions>
      </Dialog>

      {/* Tax Payment Source Toggle */}
      <Box display="flex" alignItems="center" justifyContent="center" sx={{ mt: '-6px', mb: 0.25, border: '2px solid #1976d2', borderRadius: 2, px: 2, py: 0.5, backgroundColor: '#f5f9ff', width: 'fit-content', mx: 'auto' }}>
        <Typography variant="caption" sx={{ fontWeight: 'bold', color: localTaxSource !== 'I' ? '#0d47a1' : '#999', mr: 0.5 }}>Pay Conversion Tax with Outside (Cash)</Typography>
        <Tooltip title={''} arrow>
          <span>
            <Switch
              size="small"
              checked={localTaxSource === 'I'}
              onChange={() => setTaxSourceConfirmOpen(true)}
            />
          </span>
        </Tooltip>
        <Typography variant="caption" sx={{ fontWeight: 'bold', color: localTaxSource === 'I' ? '#0d47a1' : '#999', ml: 0.5 }}>Pay Conversion Tax from Inside Traditional Account</Typography>
      </Box>
      {/* 2026-09-12: Inside payment under 59½ — penalty is folded into conv_tax by the engine.
          No dollar figure here: each conversion group and each part carries its own penalty. */}
      {localTaxSource === 'I' && conversions.some(c => (c.conv_penalty_tax || 0) > 0) && (
        <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', color: '#c62828', fontWeight: 'bold', mb: 0.25 }}>
          Conversion tax includes early-withdrawal penalty — affects Rate-Arb &amp; t<sub>C</sub>
        </Typography>
      )}

      {/* 2026-04-18: SS Torpedo bracket sizing toggle (replaces prior SS Torpedo Zone banner); 2026-04-19: blue styling when 'N' (safe default), orange when 'Y' (warning); body2 font to match Inside/Outside toggle */}
      {conversions.some(c => (c.conv_ss_taxable_delta || 0) > 0) && (
        <Box display="flex" alignItems="center" justifyContent="center" sx={{ mb: 0.5, border: localUseIRSBrackets === 'Y' ? '2px solid #e65100' : '2px solid #1976d2', borderRadius: 2, px: 2, py: 0.5, backgroundColor: localUseIRSBrackets === 'Y' ? '#fff3e0' : '#f5f9ff', width: 'fit-content', mx: 'auto' }}>
          <Typography variant="caption" sx={{ fontWeight: 'bold', color: localUseIRSBrackets !== 'Y' ? '#0d47a1' : '#999', mr: 0.5 }}>Conversion + Taxable SS fit IRS Bracket</Typography>
          <Switch
            size="small"
            checked={localUseIRSBrackets === 'Y'}
            onChange={() => setUseIRSBracketsConfirmOpen(true)}
          />
          <Typography variant="caption" sx={{ fontWeight: 'bold', color: localUseIRSBrackets === 'Y' ? '#b71c1c' : '#999', ml: 0.5 }}>Conversion fills IRS Bracket, SS spills to next bracket</Typography>
        </Box>
      )}

      {/* Conversions Table */}
      <Box display="flex" justifyContent="space-between" alignItems="center" style={{ marginBottom: '-3px' }}>
        <Typography variant="h6" sx={{ mb: 0 }}>
          Conversion Options — Decision View <Typography component="span" variant="caption" sx={{ fontStyle: 'italic', color: '#555' }}>— Each row's conversion fills the next IRS tax bracket</Typography>
        </Typography>
        <Button
          variant="text"
          color="primary"
          onClick={exportConversionsToCSV}
          size="small"
          disabled={loading || (conversions.length === 0 && parts.length === 0)}
          style={{ textTransform: 'none', fontSize: '0.875rem' }}
        >
          Export CSV
        </Button>
      </Box>
      <TableContainer component={Paper} sx={{ overflow: 'visible' }}>
        <Table size="small" sx={{ '& .MuiTableCell-root': { px: 0.5, py: 0.5 } }}>
          <TableHead>
            <TableRow>
              <TableCell align="center" colSpan={4} sx={{ fontWeight: 'bold', backgroundColor: '#ecf5fd', borderBottom: '2px solid #1976d2' }}>Conversion Details</TableCell>
              <TableCell align="center" colSpan={4} sx={{ fontWeight: 'bold', backgroundColor: '#ecf5fd', borderBottom: '2px solid #1976d2' }}>Conversion Wealth Gain (Present Value)</TableCell>
              <TableCell align="center" colSpan={4} sx={{ fontWeight: 'bold', backgroundColor: '#ecf5fd', borderBottom: '2px solid #1976d2' }}>Post-Conversion — Future Value</TableCell>
              <TableCell align="center" colSpan={2} sx={{ fontWeight: 'bold', backgroundColor: '#ecf5fd', borderBottom: '2px solid #1976d2' }}>Conversion Rates</TableCell>
            </TableRow>
            <TableRow sx={{ '& .MuiTableCell-root': { lineHeight: 1.2, py: 0.4 } }}>
              <TableCell align="center" style={{ backgroundColor: '#ecf5fd' }}></TableCell>
              <TableCell align="center" style={{ backgroundColor: '#ecf5fd' }}>Tax<br />Bracket</TableCell>
              <TableCell align="center" style={{ backgroundColor: '#ecf5fd' }}>Amount<br />Converted{conversions.some(c => (c.conv_ss_taxable_delta || 0) > 0) && <div style={{ fontSize: '0.6rem', color: inputInfo?.use_irs_brackets === 'Y' ? '#b71c1c' : '#888', lineHeight: 0.8, marginTop: -1, marginBottom: -3 }}>(taxable SS)</div>}</TableCell>
              <TableCell align="center" style={{ backgroundColor: '#c8e6c9' }}>{inputInfo?.tax_payment_source === 'I' ? <>Tax Paid<br />from Inside<br />Traditional</> : <><Tooltip title="Click for explanation" arrow {...BLUE_TOOLTIP_HINT_PROPS}><IconButton size="small" onClick={() => setShowHiddenContribExplain(!showHiddenContribExplain)} sx={{ p: 0, mr: 0, color: '#1976d2', verticalAlign: 'middle' }}><InfoIcon sx={{ fontSize: 14 }} /></IconButton></Tooltip>Tax Paid&nbsp;=<br />Hidden Roth<br />Contribution</>}</TableCell>
              <TableCell align="center" style={{ backgroundColor: '#ecf5fd', borderLeft: '3px solid #1976d2' }}>Tax‑Rate<br />Arbitrage<br /><Tooltip title="Click for explanation" arrow {...BLUE_TOOLTIP_HINT_PROPS}><IconButton size="small" onClick={() => setShowSpreadExplain(!showSpreadExplain)} sx={{ p: 0, color: '#1976d2', verticalAlign: 'middle' }}><InfoIcon sx={{ fontSize: 14 }} /></IconButton></Tooltip></TableCell>
              <TableCell align="center" style={{ backgroundColor: '#ecf5fd' }}>Medicare<br />Impact<br /><Tooltip title="Click for explanation" arrow {...BLUE_TOOLTIP_HINT_PROPS}><IconButton size="small" onClick={() => setShowMedicareExplain(!showMedicareExplain)} sx={{ p: 0, color: '#1976d2', verticalAlign: 'middle' }}><InfoIcon sx={{ fontSize: 14 }} /></IconButton></Tooltip></TableCell>
              <TableCell align="center" style={{ backgroundColor: '#ecf5fd' }}>Tax‑Drag<br />Shelter<br /><Tooltip title="Click for explanation" arrow {...BLUE_TOOLTIP_HINT_PROPS}><IconButton size="small" onClick={() => setShowTaxDragExplain(!showTaxDragExplain)} sx={{ p: 0, color: '#1976d2', verticalAlign: 'middle' }}><InfoIcon sx={{ fontSize: 14 }} /></IconButton></Tooltip></TableCell>
              <TableCell align="center" style={{ backgroundColor: '#fff3e0', fontWeight: 'bold' }}>Net<br />PV<br />(alpha)</TableCell>
              <TableCell align="center" style={{ backgroundColor: '#c8e6c9', borderLeft: '3px solid #1976d2' }}>{inputInfo?.tax_payment_source === 'I' ? <>No<br />Hidden<br />Contribution</> : <>Hidden Roth<br />Contribution<br />Growth&nbsp;<Tooltip arrow {...BLUE_TOOLTIP_PROPS} title={
                <Box>
                  <Typography variant="body2" sx={{ display: 'block', mb: 0.75, fontWeight: 'bold' }}>Hidden Roth Contribution Growth</Typography>
                  <Typography variant="body2" sx={{ display: 'block', lineHeight: 1.6 }}>
                    Net of the Hidden Contribution Tax-Drag Alpha (already reported in the Tax-Drag Shelter column). The conventional gross figure — conv_tax × Roth growth ratio — implicitly assumes a zero-growth bank counterfactual. Subtracting the drag-alpha here matches the realistic taxable-bank counterfactual and prevents double-counting the alpha. See section 4 on the Recommendation tab for the full decomposition.
                  </Typography>
                </Box>
              }><IconButton size="small" sx={{ p: 0, color: '#1976d2', verticalAlign: 'middle' }}><InfoIcon sx={{ fontSize: 14 }} /></IconButton></Tooltip></>}</TableCell>
              <TableCell align="center" style={{ backgroundColor: '#ecf5fd' }}>Net<br />FV<br />(alpha)</TableCell>
              <TableCell align="center" style={{ backgroundColor: '#ecf5fd' }}>Total<br />Spending<br />Increase</TableCell>
              <TableCell align="center" style={{ backgroundColor: '#fff3e0', fontWeight: 'bold' }}>Return<br />(IRR)</TableCell>
              <TableCell align="center" style={{ backgroundColor: '#ecf5fd', borderLeft: '3px solid #1976d2' }}>Conversion<br />Tax Rate&nbsp;<strong>t<span style={{ fontSize: '0.7em', position: 'relative', top: '0.3em' }}>C</span></strong><br /><Tooltip title="Click for explanation" arrow {...BLUE_TOOLTIP_HINT_PROPS}><IconButton size="small" onClick={() => setShowTcExplain(!showTcExplain)} sx={{ p: 0, color: '#1976d2', verticalAlign: 'middle' }}><InfoIcon sx={{ fontSize: 14 }} /></IconButton></Tooltip></TableCell>
              <TableCell align="center" style={{ backgroundColor: '#ecf5fd' }}>Distribution<br />Tax Rate&nbsp;<strong>t<span style={{ fontSize: '0.7em', position: 'relative', top: '0.3em' }}>D</span></strong><br /><Tooltip title="Click for explanation" arrow {...BLUE_TOOLTIP_HINT_PROPS}><IconButton size="small" onClick={() => setShowTdExplain(!showTdExplain)} sx={{ p: 0, color: '#1976d2', verticalAlign: 'middle' }}><InfoIcon sx={{ fontSize: 14 }} /></IconButton></Tooltip></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {showSpreadExplain && (
            <TableRow>
              <TableCell colSpan={14} sx={{ p: 0, borderBottom: 'none' }}>
                <Box sx={{ p: 1.5, backgroundColor: '#f0f7ff', borderLeft: '3px solid #1565c0', borderRadius: 1, m: 0.5 }}>
                  <Typography variant="body2" sx={{ lineHeight: 1.6, mb: 1 }}>
                    <strong>The tax-rate spread adds (or subtracts) value to the Roth at the time of conversion.</strong> The spread adds the profit from converting at a lower rate today than you would pay on future distributions. You can verify this with the algebraic form: ≈&nbsp;<Box component="span" sx={{ fontFamily: 'monospace', backgroundColor: '#e3f2fd', px: 0.5, borderRadius: 0.5 }}>Conversion Amount × (t<sub>D</sub> − t<sub>C</sub>)</Box>.
                  </Typography>
                  <Typography variant="body2" sx={{ lineHeight: 1.6, mb: 1 }}>
                    <strong>Tax-rate spread profits (losses) occur unevenly in retirement years due to the Social Security torpedo.</strong> Early retirement years can carry inflated marginal tax rates — up to 40.7% (greater than the top 37% statutory bracket) — because Social Security taxation can amplify Traditional distribution dollars by 1.5 or 1.85 according to the IRS Provisional income formula. This produces large tax savings in the years immediately following conversion. Later years drop to lower brackets as both the converted and unconverted scenarios settle into the same Social Security taxation zone. The present value overweights those valuable early years, capturing the time-value reality that the nominal sum cannot.
                  </Typography>
                  <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                    Click on any conversion below, then on the RECOMMENDATION tab under YEARLY DETAIL REPORTS select INCOME AND TAX and scroll down to the IRR Cash Flow Decomposition table to see the year-by-year details.
                  </Typography>
                </Box>
              </TableCell>
            </TableRow>
            )}
            {showTaxDragExplain && (
            <TableRow>
              <TableCell colSpan={14} sx={{ p: 0, borderBottom: 'none' }}>
                <Box sx={{ p: 1.5, backgroundColor: '#f0f7ff', borderLeft: '3px solid #1565c0', borderRadius: 1, m: 0.5 }}>
                  <Typography variant="body2" sx={{ lineHeight: 1.6, mb: 1 }}>
                    <strong>Tax-Drag Shelter is the conversion's two-pronged drag-avoidance alpha.</strong> <em>(a) Reduced RMDs:</em> conversion shrinks the Traditional balance; future distributions shrink correspondingly. Whatever portion of those avoided distributions would have been reinvested in a taxable account no longer leaks to capital gains, dividend, and state taxes. <em>(b) Hidden Contribution:</em> when conversion tax is paid from outside, that dollar moves from a taxable bank account into a tax-free Roth — also escaping the same drag. This column sums the present value of both effects: the retirement account's raison d'être, finally Quantified.
                  </Typography>
                  <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                    <strong>Reinvestment percentage and drag rate are user-configurable</strong> with sensible defaults: 100% reinvestment for RMD-only; 20% for Fixed Annuity.
                  </Typography>
                </Box>
              </TableCell>
            </TableRow>
            )}
            {showMedicareExplain && (
            <TableRow>
              <TableCell colSpan={14} sx={{ p: 0, borderBottom: 'none' }}>
                <Box sx={{ p: 1.5, backgroundColor: '#f0f7ff', borderLeft: '3px solid #1565c0', borderRadius: 1, m: 0.5 }}>
                  <Typography variant="body2" sx={{ lineHeight: 1.6, mb: 1 }}>
                    <strong>The Medicare Impact is the PV of net IRMAA premium changes caused by the conversion.</strong> For each future year, RothGPT compares the IRMAA premium with and without the conversion, sums the differences, and discounts to present value. The result can be positive (savings) or negative (cost), depending on the conversion size and the user's MAGI position relative to IRMAA bracket cliffs.
                  </Typography>
                  <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                    <strong>Two opposing forces.</strong> A conversion shrinks the Traditional balance, lowering future MAGI and saving on premiums when it drops the user below an IRMAA threshold. But in the conversion year itself, the conversion <em>raises</em> MAGI, and with IRMAA's two-year lookback, that increases premiums two years later. The reported number is the net of both effects.
                  </Typography>
                </Box>
              </TableCell>
            </TableRow>
            )}
            {showHiddenContribExplain && (
            <TableRow>
              <TableCell colSpan={14} sx={{ p: 0, borderBottom: 'none' }}>
                <Box sx={{ p: 1.5, backgroundColor: '#f0f7ff', borderLeft: '3px solid #1565c0', borderRadius: 1, m: 0.5 }}>
                  <Typography variant="body2" sx={{ lineHeight: 1.6, mb: 1 }}>
                    <strong>The conversion tax is not a cost — it's a Roth contribution in disguise.</strong> When paid from outside the Traditional account, the tax dollars relocate from a tax-leaking taxable account into the tax-free Roth wrapper. This "hidden" contribution bypasses statutory limits ($7K–$8K/yr, income phase-outs) and is the only channel through which sizable capital can be added to the Roth.
                  </Typography>
                  <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                    <strong>For inside-funded conversions</strong>, the tax comes from the Traditional account itself; no cash relocates and no contribution effect. This column shows the tax paid.
                  </Typography>
                </Box>
              </TableCell>
            </TableRow>
            )}
            {showTdExplain && (
            <TableRow>
              <TableCell colSpan={14} sx={{ p: 0, borderBottom: 'none' }}>
                <Box sx={{ p: 1.5, backgroundColor: '#f0f7ff', borderLeft: '3px solid #1565c0', borderRadius: 1, m: 0.5 }}>
                  <Typography variant="body2" sx={{ lineHeight: 1.6, mb: 1 }}>
                    <strong>t<sub>D</sub> is calculated, not assumed.</strong> Every other Roth conversion tool asks you to guess your future tax rate. RothGPT <em>derives</em> it — bottom-up from your specific distribution schedule, year by year. For each year, the model <strong>inflates</strong> tax brackets, standard deduction, and Social Security benefit to that year's nominal level; <strong>computes</strong> the Traditional distribution (per Fixed Annuity or RMD method); <strong>applies</strong> the Social Security torpedo amplification (1.5× or 1.85×) where active; and <strong>calculates</strong> federal tax owed.
                  </Typography>
                  <Typography variant="body2" sx={{ lineHeight: 1.6, mb: 1 }}>
                    Annual values are PV-weighted (discounted at portfolio return) and aggregated:&nbsp;<Box component="span" sx={{ fontFamily: 'monospace', backgroundColor: '#e3f2fd', px: 0.5, borderRadius: 0.5 }}>t<sub>D</sub> = PV(total federal tax) ÷ PV(total Traditional distributions)</Box>. Early years carry more weight — capturing the SS-torpedo years that disproportionately drive the actual realized rate.
                  </Typography>
                  <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                    <strong>Use as algebraic verification.</strong> The bottom-up tax-rate spread should agree with the shortcut&nbsp;<Box component="span" sx={{ fontFamily: 'monospace', backgroundColor: '#e3f2fd', px: 0.5, borderRadius: 0.5 }}>Conversion Amount × (t<sub>D</sub> − t<sub>C</sub>)</Box>&nbsp;to within rounding. Theoretical formula backed by empirical data — the formula is validated, not replaced.
                  </Typography>
                </Box>
              </TableCell>
            </TableRow>
            )}
            {showTcExplain && (
            <TableRow>
              <TableCell colSpan={14} sx={{ p: 0, borderBottom: 'none' }}>
                <Box sx={{ p: 1.5, backgroundColor: '#f0f7ff', borderLeft: '3px solid #1565c0', borderRadius: 1, m: 0.5 }}>
                  <Typography variant="body2" sx={{ lineHeight: 1.6, mb: 1 }}>
                    <strong>t<sub>C</sub> is the <em>effective</em> rate at which the conversion is taxed — not the IRS bracket label.</strong> RothGPT calculates the federal tax owed on the conversion and divides by the conversion amount:&nbsp;<Box component="span" sx={{ fontFamily: 'monospace', backgroundColor: '#e3f2fd', px: 0.5, borderRadius: 0.5 }}>t<sub>C</sub> = federal tax on conversion ÷ Conversion Amount</Box>.
                  </Typography>
                  <Typography variant="body2" sx={{ lineHeight: 1.6, mb: 1 }}>
                    <strong>In the marginal table</strong> (each row = one bracket filled in isolation): t<sub>C</sub> equals the nominal IRS bracket rate when no Social Security is being received in the conversion year. With SS active, the SS torpedo (1.5× or 1.85×) amplifies the rate — partially or fully — depending on how much SS gets pushed into taxable territory by that bracket's fill. With "Conversion Fills IRS Bracket" mode ON, SS spillover into the next higher bracket can amplify t<sub>C</sub> further still.
                  </Typography>
                  <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                    <strong>In the cumulative table</strong> (each row = stacked sum of brackets up through that level): t<sub>C</sub> is a weighted average across all marginal brackets the conversion has filled. It never matches a single IRS bracket label — it's the blended cost. A "22%" row in the cumulative table is the <em>bracket label up to which</em> the conversion fills, not the rate paid.
                  </Typography>
                </Box>
              </TableCell>
            </TableRow>
            )}
            {conversions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={14}>No conversions data available</TableCell>
              </TableRow>
            ) : (
              conversions.map((conv) => (
                  <TableRow
                    key={conv.conv_group_num}
                    style={getConversionsRowStyle(conv)}
                    onClick={() => onViewConversion && conv.conv_amt > 0 && onViewConversion(conv, conv.conv_group_num === recommendedConversion?.conv_group_num)}
                    onMouseEnter={() => setHoveredConvGroupNum(conv.conv_group_num)}
                    onMouseLeave={() => setHoveredConvGroupNum(null)}
                    sx={{
                      cursor: conv.conv_amt > 0 && onViewConversion ? 'pointer' : 'default',
                      '&:hover': conv.conv_amt > 0 && onViewConversion ? {
                        backgroundColor: '#e3f2fd !important',
                      } : {},
                      '& td:hover': conv.conv_amt > 0 && onViewConversion ? CELL_HOVER_SPOTLIGHT : {},
                    }}
                  >
                  <TableCell align="right">{conv.conv_group_num}</TableCell>
                  <TableCell align="right">{(conv.tax_rate_bucket * 100).toFixed(1)}%</TableCell>
                  <TableCell align="right">
                    ${conv.conv_amt.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}{isPartialBracket(conv, conversions) && <sup>*</sup>}
                    {(conv.conv_ss_taxable_delta || 0) > 0 && <div style={{ fontSize: '0.6rem', color: inputInfo?.use_irs_brackets === 'Y' ? '#b71c1c' : '#888', lineHeight: 0.8, marginTop: -2, marginBottom: -4 }}>${Math.round(conv.conv_ss_taxable_delta).toLocaleString()} SS</div>}
                  </TableCell>
                  <TableCell align="right">
                    ${conv.conv_tax.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                  </TableCell>
                  <TableCell align="right" style={{ borderLeft: '3px solid #1976d2' }}>
                    ${Math.round(conv.spread_pv_tw || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                  </TableCell>
                  <TableCell align="right">
                    ${Math.round(conv.irmaa_savings_pv || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                  </TableCell>
                  <TableCell align="right">
                    ${Math.round((conv.tax_drag_rmd_pv || 0) + srcShelterPv(conv)).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                  </TableCell>
                  {(() => {
                    // 2026-09-27: engine-computed, not summed here. Under Inside funding it
                    // correctly omits the SRC shelter, which this sum could not know to do.
                    const convNetPV = conv.conversion_alpha_pv || 0;
                    return (
                      <TableCell align="right" style={{ fontWeight: 'bold', color: convNetPV >= 0 ? '#2e7d32' : '#d32f2f' }}>
                        ${Math.round(convNetPV).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                      </TableCell>
                    );
                  })()}
                  <TableCell align="right" style={{ borderLeft: '3px solid #1976d2' }}>
                    ${(Math.round(conv.hidden_cont_fv || 0) + 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                  </TableCell>
                  {(() => {
                    const convAlphaFV = conv.conversion_alpha_fv || 0;
                    return (
                      <TableCell align="right" style={{ color: convAlphaFV >= 0 ? '#2e7d32' : '#d32f2f' }}>
                        ${Math.round(convAlphaFV).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                      </TableCell>
                    );
                  })()}
                  <TableCell align="right">
                    ${conv.total_after_tax_dist_chg_amt.toLocaleString('en-US', {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                    })}
                  </TableCell>
                  <TableCell align="right" style={{ fontWeight: 'bold', color: conv.conv_irr > (inputInfo?.dist_return_assum || 0) ? '#2e7d32' : conv.conv_irr < (inputInfo?.dist_return_assum || 0) ? '#d32f2f' : 'inherit' }}>{(conv.conv_irr * 100).toFixed(2)}%</TableCell>
                  <TableCell align="right" style={{ borderLeft: '3px solid #1976d2' }}>{(conv.conv_tax_rate * 100).toFixed(1)}%</TableCell>
                  <TableCell align="right">{(conv.conv_dist_tax_rate_tw * 100).toFixed(1)}%</TableCell>
                  </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {/* 2026-04-19: Yellow explanation box under top table — only when 'Y' toggle is on AND SS torpedo is active */}
      {inputInfo?.use_irs_brackets === 'Y' && conversions.some(c => (c.conv_ss_taxable_delta || 0) > 0) && (
        <Box sx={{ mt: 1, px: 1, py: 0.25, backgroundColor: '#fff8e1', borderLeft: '3px solid #ff8f00', borderRadius: 1, width: 'fit-content', mx: 'auto' }}>
          <Typography variant="caption" sx={{ color: '#6d4c00' }}>
            Each conversion above fills its IRS bracket — the triggered taxable Social Security (shown in red) spills into the next higher-rate bracket.
          </Typography>
        </Box>
      )}
      {/* Parts Table */}
      <Typography variant="h6" sx={{ mt: (inputInfo?.use_irs_brackets === 'Y' && conversions.some(c => (c.conv_ss_taxable_delta || 0) > 0)) ? 0.25 : 1.5, lineHeight: 1.2 }}>
        {getPartsAccordionSummary()} <Typography component="span" variant="caption" sx={{ fontStyle: 'italic', color: '#555' }}>— Marginal impact of adding each bracket to a conversion</Typography>
      </Typography>
          <TableContainer component={Paper}>
        <Table size="small" sx={{ '& .MuiTableCell-root': { px: 0.5, py: 0.5 }, '& tbody td:hover': CELL_HOVER_SPOTLIGHT }}>
          <TableHead>
            <TableRow>
              <TableCell align="center" colSpan={4} sx={{ fontWeight: 'bold', backgroundColor: '#ecf5fd', borderBottom: '2px solid #1976d2' }}>Conversion Details</TableCell>
              <TableCell align="center" colSpan={4} sx={{ fontWeight: 'bold', backgroundColor: '#ecf5fd', borderBottom: '2px solid #1976d2' }}>Conversion Wealth Gain (Present Value)</TableCell>
              <TableCell align="center" colSpan={4} sx={{ fontWeight: 'bold', backgroundColor: '#ecf5fd', borderBottom: '2px solid #1976d2' }}>Post-Conversion — Future Value</TableCell>
              <TableCell align="center" colSpan={2} sx={{ fontWeight: 'bold', backgroundColor: '#ecf5fd', borderBottom: '2px solid #1976d2' }}>Conversion Rates</TableCell>
            </TableRow>
            <TableRow sx={{ '& .MuiTableCell-root': { lineHeight: 1.2, py: 0.4 } }}>
              <TableCell align="center" style={{ backgroundColor: '#ecf5fd' }}></TableCell>
              <TableCell align="center" style={{ backgroundColor: '#ecf5fd' }}>Tax<br />Bracket</TableCell>
              <TableCell align="center" style={{ backgroundColor: '#ecf5fd' }}>Amount<br />Converted{conversions.some(c => (c.conv_ss_taxable_delta || 0) > 0) && <div style={{ fontSize: '0.6rem', color: inputInfo?.use_irs_brackets === 'Y' ? '#b71c1c' : '#888', lineHeight: 0.8, marginTop: -1, marginBottom: -3 }}>(taxable SS)</div>}</TableCell>
              <TableCell align="center" style={{ backgroundColor: '#c8e6c9' }}>{inputInfo?.tax_payment_source === 'I' ? <>Tax Paid<br />from Inside<br />Traditional</> : <>Tax Paid&nbsp;=<br />Hidden Roth<br />Contribution</>}</TableCell>
              <TableCell align="center" style={{ backgroundColor: '#ecf5fd', borderLeft: '3px solid #1976d2' }}>Tax‑Rate<br />Arbitrage</TableCell>
              <TableCell align="center" style={{ backgroundColor: '#ecf5fd' }}>Medicare<br />Impact</TableCell>
              <TableCell align="center" style={{ backgroundColor: '#ecf5fd' }}>Tax‑Drag<br />Shelter</TableCell>
              <TableCell align="center" style={{ backgroundColor: '#fff3e0', fontWeight: 'bold' }}>Net<br />PV<br />(alpha)</TableCell>
              <TableCell align="center" style={{ backgroundColor: '#c8e6c9', borderLeft: '3px solid #1976d2' }}>{inputInfo?.tax_payment_source === 'I' ? <>No<br />Hidden<br />Contribution</> : <>Hidden Roth<br />Contribution<br />Growth&nbsp;<Tooltip arrow {...BLUE_TOOLTIP_PROPS} title={
                <Box>
                  <Typography variant="body2" sx={{ display: 'block', mb: 0.75, fontWeight: 'bold' }}>Hidden Roth Contribution Growth</Typography>
                  <Typography variant="body2" sx={{ display: 'block', lineHeight: 1.6 }}>
                    Net of the Hidden Contribution Tax-Drag Alpha (already reported in the Tax-Drag Shelter column). The conventional gross figure — conv_tax × Roth growth ratio — implicitly assumes a zero-growth bank counterfactual. Subtracting the drag-alpha here matches the realistic taxable-bank counterfactual and prevents double-counting the alpha. See section 4 on the Recommendation tab for the full decomposition.
                  </Typography>
                </Box>
              }><IconButton size="small" sx={{ p: 0, color: '#1976d2', verticalAlign: 'middle' }}><InfoIcon sx={{ fontSize: 14 }} /></IconButton></Tooltip></>}</TableCell>
              <TableCell align="center" style={{ backgroundColor: '#ecf5fd' }}>Net<br />FV<br />(alpha)</TableCell>
              <TableCell align="center" style={{ backgroundColor: '#ecf5fd' }}>Total<br />Spending<br />Increase</TableCell>
              <TableCell align="center" style={{ backgroundColor: '#fff3e0', fontWeight: 'bold' }}>Return<br />(IRR)</TableCell>
              <TableCell align="center" style={{ backgroundColor: '#ecf5fd', borderLeft: '3px solid #1976d2' }}>Conversion<br />Tax Rate&nbsp;<strong>t<sub>C</sub></strong></TableCell>
              <TableCell align="center" style={{ backgroundColor: '#ecf5fd' }}>Distribution<br />Tax Rate&nbsp;<strong>t<sub>D</sub></strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {parts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={14}>No parts data available</TableCell>
              </TableRow>
            ) : (
              parts.map((part) => (
                <TableRow
                  key={part.conv_group_num}
                  style={getPartsRowStyle(part)}
                  sx={hoveredConvGroupNum !== null && part.conv_group_num <= hoveredConvGroupNum && part.conv_amt > 0 ? {
                    boxShadow: 'inset 3px 0 0 #1976d2, inset -3px 0 0 #1976d2' +
                      (part.conv_group_num === parts.find(p => p.conv_amt > 0)?.conv_group_num ? ', inset 0 3px 0 #1976d2' : '') +
                      (part.conv_group_num === hoveredConvGroupNum ? ', inset 0 -3px 0 #1976d2' : ''),
                  } : {}}
                >
                  <TableCell align="right">{part.conv_group_num}</TableCell>
                  <TableCell align="right">{(part.tax_rate_bucket * 100).toFixed(1)}%</TableCell>
                  <TableCell align="right">
                    ${part.conv_amt.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}{isPartialBracket(part, parts) && <sup>*</sup>}
                    {(part.conv_ss_taxable_delta || 0) > 0 && <div style={{ fontSize: '0.6rem', color: inputInfo?.use_irs_brackets === 'Y' ? '#b71c1c' : '#888', lineHeight: 0.8, marginTop: -2, marginBottom: -4 }}>${Math.round(part.conv_ss_taxable_delta).toLocaleString()} SS</div>}
                  </TableCell>
                  <TableCell align="right">
                    ${part.conv_tax.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                  </TableCell>
                  <TableCell align="right" style={{ borderLeft: '3px solid #1976d2' }}>
                    ${Math.round(part.spread_pv_tw || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                  </TableCell>
                  <TableCell align="right">
                    ${Math.round(part.irmaa_savings_pv || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                  </TableCell>
                  <TableCell align="right">
                    ${Math.round((part.tax_drag_rmd_pv || 0) + srcShelterPv(part)).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                  </TableCell>
                  {(() => {
                    const partNetPV = part.conversion_alpha_pv || 0;
                    return (
                      <TableCell align="right" style={{ fontWeight: 'bold', color: partNetPV >= 0 ? '#2e7d32' : '#d32f2f' }}>
                        ${Math.round(partNetPV).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                      </TableCell>
                    );
                  })()}
                  <TableCell align="right" style={{ borderLeft: '3px solid #1976d2' }}>
                    ${(Math.round(part.hidden_cont_fv || 0) + 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                  </TableCell>
                  {(() => {
                    const partAlphaFV = part.conversion_alpha_fv || 0;
                    return (
                      <TableCell align="right" style={{ color: partAlphaFV >= 0 ? '#2e7d32' : '#d32f2f' }}>
                        ${Math.round(partAlphaFV).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                      </TableCell>
                    );
                  })()}
                  <TableCell align="right">
                    ${part.total_after_tax_dist_chg_amt.toLocaleString('en-US', {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                    })}
                  </TableCell>
                  <TableCell align="right" style={{ fontWeight: 'bold', color: part.conv_irr > (inputInfo?.dist_return_assum || 0) ? '#2e7d32' : part.conv_irr < (inputInfo?.dist_return_assum || 0) ? '#d32f2f' : 'inherit' }}>{(part.conv_irr * 100).toFixed(2)}%</TableCell>
                  <TableCell align="right" style={{ borderLeft: '3px solid #1976d2' }}>{(part.conv_tax_rate * 100).toFixed(1)}%</TableCell>
                  <TableCell align="right">{(part.conv_dist_tax_rate_tw * 100).toFixed(1)}%</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

          <TermsDefinitions />

      {inputInfo?.taxable_inc_when_convert > 0 && (
        <Typography variant="body2" style={{ marginTop: '4px', marginBottom: '2px', fontStyle: 'italic', color: '#666' }}>
          *  Partial bracket available only. ${Number(inputInfo.taxable_inc_when_convert).toLocaleString()} current taxable income fills lower bracket(s), raising the conversion tax rate
        </Typography>
      )}
      {inputInfo?.taxable_inc_when_distribute > 0 && (
        <Typography variant="body2" style={{ marginBottom: '8px', fontStyle: 'italic', color: '#666' }}>
          †  ${Number(inputInfo.taxable_inc_when_distribute).toLocaleString()} other taxable income in retirement fills lower bracket(s), raising the distribution tax rate{inputInfo?.distribution_method === 'R' && inputInfo?.beneficiary_taxable_income > 0 && `; $${Number(inputInfo.beneficiary_taxable_income).toLocaleString()} beneficiary income has same effect`}
        </Typography>
      )}
      {!inputInfo?.taxable_inc_when_distribute && inputInfo?.distribution_method === 'R' && inputInfo?.beneficiary_taxable_income > 0 && (
        <Typography variant="body2" style={{ marginBottom: '8px', fontStyle: 'italic', color: '#666' }}>
          †  ${Number(inputInfo.beneficiary_taxable_income).toLocaleString()} beneficiary income after death fills lower bracket(s), raising the distribution tax rate
        </Typography>
      )}

      <Snackbar
        open={!!successMessage}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
      >
        <Alert severity="success" onClose={handleSnackbarClose}>
          {successMessage}
        </Alert>
      </Snackbar>

      {/* Payment Modal */}
      <PaymentModal
        open={paymentModalOpen}
        onClose={() => setPaymentModalOpen(false)}
        calcCount={calcCount}
        userId={userId}
        onFreeSelected={async () => {
          try {
            const result = await onRunCalculations(userId);
            setCalcCount(result.calcCount);
            onRefetchUser();
          } catch (err) {
            setErrorMessage(err.message || 'Calculation failed. Please try again.');
          }
        }}
      />
    </>
  );
};

export default ConversionsTab;